import { parse as parseYaml } from 'yaml'

/** Values the card dropdown writes. Anything else already in the fence stays selectable. */
export const BASECAMP_DRAFT_STATUSES = ['not posted', 'posted'] as const

export type BasecampDraft = {
  label: string
  status: string
  /** Where this draft should be pasted. Often the work spec todo, not always. */
  targetUrl: string
  body: string
}

const FENCE_RE = /```(?:yaml[ \t]+basecamp-draft|basecamp-draft)\s*\r?\n([\s\S]*?)\r?\n```/i
const KIND_RE = /^kind:\s*basecamp-draft\s*$/m

function asText(value: unknown): string {
  if (value == null) return ''
  return String(value).trim().replace(/^['"]|['"]$/g, '')
}

/** http(s) only. Empty when the header value is missing or not a URL. */
export function draftTargetUrl(value: unknown): string {
  const raw = asText(value)
  if (!/^https?:\/\//i.test(raw)) return ''
  try {
    const url = new URL(raw)
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return ''
    return url.toString()
  } catch {
    return ''
  }
}

export function isBasecampTargetUrl(url: string): boolean {
  try {
    const host = new URL(url).hostname.toLowerCase()
    return host === 'basecamp.com' || host.endsWith('.basecamp.com')
  } catch {
    return false
  }
}

function headerUrl(header: string): string {
  const target = header.match(/^target_url:\s*(.*)$/m)?.[1]
  const url = header.match(/^url:\s*(.*)$/m)?.[1]
  return draftTargetUrl(target || url || '')
}

function unwrapFence(raw: string): string {
  const match = raw.match(FENCE_RE)
  return (match ? match[1] : raw).replace(/\s+$/, '')
}

/**
 * TipTap language attr (`yaml`, `basecamp-draft`) or a highlight class
 * (`hljs language-yaml`). Empty language still matches when `kind` is set.
 */
export function isBasecampDraftFenceLanguage(language: string, text: string): boolean {
  const raw = (language || '').toLowerCase().trim()
  if (raw.includes('basecamp-draft')) return true
  const yamlFence =
    raw === '' ||
    raw === 'yaml' ||
    raw.startsWith('yaml ') ||
    raw.includes('language-yaml')
  if (!yamlFence) return false
  return KIND_RE.test(text)
}

/**
 * Parse a basecamp-draft fence body.
 * Preferred shape: header keys, a `---` line, then the paste body (no YAML indent).
 * Also accepts `body: |` so the whole fence is one YAML document.
 */
export function parseBasecampDraftFence(raw: string, language = ''): BasecampDraft | null {
  if (!raw?.trim()) return null
  const src = unwrapFence(raw)
  if (!isBasecampDraftFenceLanguage(language, src) && !KIND_RE.test(src)) return null

  if (/^body:\s*[|>][+-]?/m.test(src)) {
    let parsed: unknown
    try {
      parsed = parseYaml(src)
    } catch {
      return null
    }
    if (!parsed || typeof parsed !== 'object') return null
    const record = parsed as Record<string, unknown>
    const body = typeof record.body === 'string' ? record.body.replace(/\s+$/, '') : ''
    if (!body.trim()) return null
    return {
      label: asText(record.label),
      status: asText(record.status),
      targetUrl: draftTargetUrl(record.target_url || record.url),
      body,
    }
  }

  const sep = src.search(/^\s*---\s*$/m)
  if (sep >= 0) {
    const header = src.slice(0, sep)
    const body = src.slice(sep).replace(/^\s*---\s*\r?\n?/, '').replace(/\s+$/, '')
    if (!body.trim()) return null
    return {
      label: asText(header.match(/^label:\s*(.*)$/m)?.[1]),
      status: asText(header.match(/^status:\s*(.*)$/m)?.[1]),
      targetUrl: headerUrl(header),
      body,
    }
  }

  if ((language || '').toLowerCase().includes('basecamp-draft')) {
    return { label: '', status: '', targetUrl: '', body: src.trim() }
  }
  return null
}

/**
 * Rewrite the unindented `status:` line in one fence body.
 * Inserts it before `---` (or after the first line) when the key is missing.
 */
export function withBasecampDraftStatus(fenceSource: string, status: string): string {
  const next = status.trim()
  if (!next) return fenceSource
  const src = fenceSource.replace(/\r\n/g, '\n')
  if (/^status:\s*/m.test(src)) {
    return src.replace(/^status:\s*.*$/m, `status: ${next}`)
  }
  const sep = src.search(/^\s*---\s*$/m)
  if (sep >= 0) {
    const header = src.slice(0, sep).replace(/\s*$/, '')
    return `${header}\nstatus: ${next}\n${src.slice(sep)}`
  }
  const lineBreak = src.indexOf('\n')
  if (lineBreak >= 0) {
    return `${src.slice(0, lineBreak)}\nstatus: ${next}${src.slice(lineBreak)}`
  }
  return `${src}\nstatus: ${next}`
}

/**
 * Replace one fence inside a document. Returns null when the fence is missing
 * or the status is already that value.
 */
/** Markdown fence ready to append to a work spec. */
export function basecampDraftFenceMarkdown(fenceSource: string): string {
  const body = fenceSource.replace(/\r\n/g, '\n').replace(/\s+$/, '')
  return '```yaml basecamp-draft\n' + body + '\n```'
}

export function documentHasBasecampDraft(content: string, fenceSource: string): boolean {
  const needle = fenceSource.replace(/\r\n/g, '\n').trim()
  return needle.length > 0 && content.replace(/\r\n/g, '\n').includes(needle)
}

export function appendBasecampDraftFence(content: string, fenceSource: string): string {
  const block = basecampDraftFenceMarkdown(fenceSource)
  const base = content.replace(/\s+$/, '')
  return `${base}\n\n${block}\n`
}

export function applyBasecampDraftStatus(
  documentContent: string,
  fenceSource: string,
  status: string,
): string | null {
  const needle = fenceSource.replace(/\r\n/g, '\n').replace(/\n$/, '')
  const nextFence = withBasecampDraftStatus(needle, status)
  if (nextFence === needle) return null
  const idx = documentContent.indexOf(needle)
  if (idx < 0) return null
  return documentContent.slice(0, idx) + nextFence + documentContent.slice(idx + needle.length)
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

const CODE_SLOT = '\u0000'

/** Inline marks the comment composer keeps: code, links, strike, bold, italic. */
function inlineHtml(line: string): string {
  let text = escapeHtml(line)
  const codes: string[] = []
  text = text.replace(/`([^`]+)`/g, (_match, code: string) => {
    codes.push(`<code>${code}</code>`)
    return `${CODE_SLOT}${codes.length - 1}${CODE_SLOT}`
  })
  text = text.replace(/\[([^\]]+)\]\((https?:\/\/[^)\s]+)\)/g, '<a href="$2">$1</a>')
  text = text.replace(/~~([^~]+)~~/g, '<s>$1</s>')
  text = text.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
  text = text.replace(/(^|[^*])\*([^*\n]+)\*/g, '$1<em>$2</em>')
  text = text.replace(/(^|\s)_([^_\n]+)_(?=\s|$)/g, '$1<em>$2</em>')
  text = text.replace(new RegExp(`${CODE_SLOT}(\\d+)${CODE_SLOT}`, 'g'), (_match, index: string) => {
    return codes[Number(index)] || ''
  })
  return text
}

type DraftBlock =
  | { kind: 'p'; lines: string[] }
  | { kind: 'h'; level: number; text: string }
  | { kind: 'ol' | 'ul'; items: string[] }
  | { kind: 'quote'; lines: string[] }
  | { kind: 'pre'; text: string }
  | { kind: 'table'; headers: string[]; rows: string[][] }

function isBlank(line: string): boolean {
  return line.trim() === ''
}

function splitCells(line: string): string[] {
  let raw = line.trim()
  if (raw.startsWith('|')) raw = raw.slice(1)
  if (raw.endsWith('|')) raw = raw.slice(0, -1)
  return raw.split('|').map((cell) => cell.trim())
}

function isTableSeparator(line: string): boolean {
  const cells = splitCells(line)
  return cells.length >= 2 && cells.every((cell) => /^:?-{3,}:?$/.test(cell))
}

function looksLikeTable(lines: string[], index: number): boolean {
  const header = lines[index] || ''
  const sep = lines[index + 1] || ''
  return header.includes('|') && isTableSeparator(sep)
}

/** Walk markdown into blocks. Blank lines separate paragraphs; they are not copied. */
function draftBlocks(normalized: string): DraftBlock[] {
  const lines = normalized.split('\n')
  const blocks: DraftBlock[] = []
  let i = 0
  while (i < lines.length) {
    if (isBlank(lines[i])) {
      i += 1
      continue
    }
    if (lines[i].startsWith('```')) {
      const body: string[] = []
      i += 1
      while (i < lines.length && !lines[i].startsWith('```')) {
        body.push(lines[i])
        i += 1
      }
      if (i < lines.length) i += 1
      blocks.push({ kind: 'pre', text: body.join('\n') })
      continue
    }
    if (looksLikeTable(lines, i)) {
      const headers = splitCells(lines[i])
      i += 2
      const rows: string[][] = []
      while (i < lines.length && lines[i].includes('|') && !isBlank(lines[i])) {
        rows.push(splitCells(lines[i]))
        i += 1
      }
      blocks.push({ kind: 'table', headers, rows })
      continue
    }
    const heading = lines[i].match(/^(#{1,4})\s+(\S.*)$/)
    if (heading) {
      blocks.push({ kind: 'h', level: heading[1].length, text: heading[2].trim() })
      i += 1
      continue
    }
    if (/^>\s?/.test(lines[i])) {
      const quote: string[] = []
      while (i < lines.length && /^>\s?/.test(lines[i])) {
        quote.push(lines[i].replace(/^>\s?/, ''))
        i += 1
      }
      blocks.push({ kind: 'quote', lines: quote })
      continue
    }
    if (/^\d+\.\s+\S/.test(lines[i].trim())) {
      const items: string[] = []
      while (i < lines.length && /^\d+\.\s+\S/.test(lines[i].trim())) {
        items.push(lines[i].trim().replace(/^\d+\.\s+/, ''))
        i += 1
      }
      blocks.push({ kind: 'ol', items })
      continue
    }
    if (/^[-*]\s+\S/.test(lines[i].trim())) {
      const items: string[] = []
      while (i < lines.length && /^[-*]\s+\S/.test(lines[i].trim())) {
        items.push(lines[i].trim().replace(/^[-*]\s+/, ''))
        i += 1
      }
      blocks.push({ kind: 'ul', items })
      continue
    }
    const para: string[] = []
    while (
      i < lines.length &&
      !isBlank(lines[i]) &&
      !lines[i].startsWith('```') &&
      !looksLikeTable(lines, i) &&
      !/^(#{1,4})\s+\S/.test(lines[i]) &&
      !/^>\s?/.test(lines[i]) &&
      !/^\d+\.\s+\S/.test(lines[i].trim()) &&
      !/^[-*]\s+\S/.test(lines[i].trim())
    ) {
      para.push(lines[i])
      i += 1
    }
    blocks.push({ kind: 'p', lines: para })
  }
  return blocks
}

/** Card preview. Block spacing comes from CSS, not the paste breaks Lexical needs. */
export function basecampDraftPreviewHtml(body: string): string {
  const normalized = body
    .replace(/\r\n/g, '\n')
    .replace(/\u2014/g, ' - ')
    .replace(/\u2013/g, '-')
    .replace(/\u2026/g, '...')
    .trim()
  if (!normalized) return ''
  return draftBlocks(normalized)
    .map((block) => {
      if (block.kind === 'p') {
        return `<p>${block.lines.map((line) => inlineHtml(line)).join('<br>')}</p>`
      }
      return renderBlock(block)
    })
    .filter(Boolean)
    .join('')
}

function renderBlock(block: DraftBlock): string {
  if (block.kind === 'h') {
    return `<h${block.level}>${inlineHtml(block.text)}</h${block.level}>`
  }
  if (block.kind === 'ol' || block.kind === 'ul') {
    const tag = block.kind
    const items = block.items.map((item) => `<li>${inlineHtml(item)}</li>`).join('')
    return `<${tag}>${items}</${tag}>`
  }
  if (block.kind === 'quote') {
    return `<blockquote>${block.lines.map((line) => inlineHtml(line)).join('<br>')}</blockquote>`
  }
  if (block.kind === 'pre') {
    return `<pre><code>${escapeHtml(block.text)}</code></pre>`
  }
  if (block.kind === 'table') {
    const head = block.headers.map((cell) => `<th>${inlineHtml(cell)}</th>`).join('')
    const body = block.rows
      .map((row) => `<tr>${row.map((cell) => `<td>${inlineHtml(cell)}</td>`).join('')}</tr>`)
      .join('')
    return `<table><thead><tr>${head}</tr></thead><tbody>${body}</tbody></table>`
  }
  return block.lines.map((line) => inlineHtml(line)).join('<br>')
}

function isStructural(html: string): boolean {
  return /^<(ol|ul|h[1-4]|table|blockquote|pre)\b/.test(html)
}

/**
 * HTML for a Basecamp comment paste (Lexical).
 * Paragraphs use `<br><br>`, not `<p>` (a paragraph tag adds a second gap).
 * A list, heading, table, or quote gets one `<br>` after the previous
 * paragraph. Two breaks there become a blank line plus the block; none
 * glues the block to the paragraph.
 */
export function basecampDraftToHtml(body: string): string {
  const normalized = body
    .replace(/\r\n/g, '\n')
    .replace(/\u2014/g, ' - ')
    .replace(/\u2013/g, '-')
    .replace(/\u2026/g, '...')
    .trim()
  if (!normalized) return ''

  const parts = draftBlocks(normalized).map(renderBlock).filter(Boolean)
  let html = ''
  for (let i = 0; i < parts.length; i += 1) {
    if (i > 0) {
      if (!isStructural(parts[i])) html += '<br><br>'
      else if (!isStructural(parts[i - 1])) html += '<br>'
    }
    html += parts[i]
  }
  return html
}

export function basecampDraftPlain(body: string): string {
  return body
    .replace(/\r\n/g, '\n')
    .replace(/\u2014/g, ' - ')
    .replace(/\u2013/g, '-')
    .replace(/\u2026/g, '...')
    .replace(/^>\s?/gm, '')
    .trim()
}

function copyViaSelection(html: string, plain: string): boolean {
  const host = document.createElement('div')
  host.setAttribute('contenteditable', 'true')
  host.style.position = 'fixed'
  host.style.left = '-9999px'
  host.style.top = '0'
  host.innerHTML = html || escapeHtml(plain).replace(/\n/g, '<br>')
  document.body.appendChild(host)
  const range = document.createRange()
  range.selectNodeContents(host)
  const selection = window.getSelection()
  selection?.removeAllRanges()
  selection?.addRange(range)
  let ok = false
  try {
    ok = document.execCommand('copy')
  } catch {
    ok = false
  }
  selection?.removeAllRanges()
  host.remove()
  return ok
}

/** Copy comment HTML plus a plain-text fallback. */
export async function copyBasecampDraft(draft: BasecampDraft): Promise<boolean> {
  const plain = basecampDraftPlain(draft.body)
  const html = basecampDraftToHtml(draft.body)
  try {
    if (typeof ClipboardItem !== 'undefined' && navigator.clipboard?.write) {
      await navigator.clipboard.write([
        new ClipboardItem({
          'text/html': new Blob([html], { type: 'text/html' }),
          'text/plain': new Blob([plain], { type: 'text/plain' }),
        }),
      ])
      return true
    }
  } catch {
    // Fall through to a real selection copy, which Trix accepts.
  }
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(plain)
      return true
    } catch {
      // execCommand below
    }
  }
  return copyViaSelection(html, plain)
}
